/**
 * 动态 SEO + GEO meta 管理工具
 * 用于各页面动态设置 title、description、keywords、Open Graph 和 GEO 标签
 */
import { isBrowser } from '@/utils/ssr'

export interface SeoMeta {
  title?: string
  description?: string
  keywords?: string
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
  ogUrl?: string
  /** GEO: 纬度,经度 (用于 ICBM) */
  geoPosition?: string
  geoPlacename?: string
  geoRegion?: string
}

const SITE_NAME = '星际高球联盟'

function setMetaTag(name: string, content: string, attr = 'name') {
  if (!content || !isBrowser) return
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null
  if (el) {
    el.content = content
  } else {
    el = document.createElement('meta')
    el.setAttribute(attr, name)
    el.content = content
    document.head.appendChild(el)
  }
}

function setCanonical(url: string) {
  if (!url || !isBrowser) return
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
  if (el) {
    el.href = url
  } else {
    el = document.createElement('link')
    el.rel = 'canonical'
    el.href = url
    document.head.appendChild(el)
  }
}

export function useSeoMeta(meta: SeoMeta) {
  if (!isBrowser) return
  const suffix = ` - ${SITE_NAME}`

  // Title
  if (meta.title) {
    document.title = meta.title.includes(SITE_NAME) ? meta.title : meta.title + suffix
  }

  // 基础 SEO
  if (meta.description) setMetaTag('description', meta.description)
  if (meta.keywords) setMetaTag('keywords', meta.keywords)

  // Open Graph
  setMetaTag('og:title', meta.ogTitle || meta.title || '', 'property')
  setMetaTag('og:description', meta.ogDescription || meta.description || '', 'property')
  if (meta.ogImage) setMetaTag('og:image', meta.ogImage, 'property')
  if (meta.ogUrl) {
    setMetaTag('og:url', meta.ogUrl, 'property')
    setCanonical(meta.ogUrl)
  }

  // GEO 标签
  if (meta.geoPosition) setMetaTag('ICBM', meta.geoPosition)
  if (meta.geoPlacename) setMetaTag('geo.placename', meta.geoPlacename)
  if (meta.geoRegion) setMetaTag('geo.region', meta.geoRegion)
}

/**
 * 生成 JSON-LD 结构化数据并注入 head
 * @param data LD+JSON 数据对象
 */
export function useJsonLd(data: Record<string, any>) {
  if (!isBrowser) return
  // 移除旧的 JSON-LD
  const old = document.querySelector('script[data-seo="jsonld"]')
  if (old) old.remove()

  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.setAttribute('data-seo', 'jsonld')
  script.textContent = JSON.stringify(data)
  document.head.appendChild(script)
}

/**
 * 生成高尔夫球场的 JSON-LD 结构化数据（Schema.org GolfCourse）
 */
export function buildGolfCourseJsonLd(opts: {
  name: string
  description?: string
  address?: string
  image?: string
  telephone?: string
  url?: string
  geo?: { lat: number; lng: number }
  priceRange?: string
}) {
  const ld: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'GolfCourse',
    name: opts.name,
  }
  if (opts.description) ld.description = opts.description
  if (opts.image) ld.image = opts.image
  if (opts.telephone) ld.telephone = opts.telephone
  if (opts.url) ld.url = opts.url
  if (opts.priceRange) ld.priceRange = opts.priceRange
  if (opts.address) {
    ld.address = {
      '@type': 'PostalAddress',
      streetAddress: opts.address,
      addressCountry: 'CN',
    }
  }
  if (opts.geo) {
    ld.geo = {
      '@type': 'GeoCoordinates',
      latitude: opts.geo.lat,
      longitude: opts.geo.lng,
    }
  }
  return ld
}
